import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

/**
 * A single-page, print-friendly compliance summary the campaign can attach
 * to grant applications. It documents the WCAG 2.2 AA measures implemented,
 * bilingual English/Welsh readiness, privacy posture and content sourcing.
 * Kept intentionally plain so the printed output fits on one A4 page.
 */

function Row({ label, value, testid }) {
  return (
    <div
      className="grid grid-cols-[130px_1fr] gap-3 py-2"
      style={{ borderBottom: '1px solid var(--tf-card-border)' }}
      data-testid={testid}
    >
      <p
        className="font-body font-bold"
        style={{
          fontSize: 10.5,
          letterSpacing: '0.2em',
          color: 'var(--tf-purple)',
          paddingTop: 3,
        }}
      >
        {label}
      </p>
      <p
        className="font-body"
        style={{ fontSize: 12.5, color: 'var(--tf-text)', lineHeight: 1.55 }}
      >
        {value}
      </p>
    </div>
  );
}

function CheckItem({ children }) {
  return (
    <li
      className="flex items-start gap-2 py-1"
      style={{ fontSize: 12.5, color: 'var(--tf-text)', lineHeight: 1.55 }}
    >
      <CheckCircle2
        size={14}
        strokeWidth={2.2}
        color="var(--tf-purple)"
        style={{ marginTop: 3, flexShrink: 0 }}
        aria-hidden="true"
      />
      <span className="font-body">{children}</span>
    </li>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <section className="mt-6 tf-compliance-section">
      {eyebrow && (
        <p
          className="font-body font-bold"
          style={{
            fontSize: 10,
            letterSpacing: '0.24em',
            color: 'var(--tf-purple)',
          }}
        >
          {eyebrow}
        </p>
      )}
      <h2
        className="font-heading mt-1"
        style={{
          fontSize: 17,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
          lineHeight: 1.25,
        }}
      >
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default function Compliance() {
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="tf-compliance-page py-8 sm:py-10" data-testid="compliance-screen">
      <SEO
        title="Accessibility & bilingual readiness"
        description="A one-page summary of the WCAG 2.2 AA accessibility measures and Welsh-language readiness of The Full Impact — designed to accompany grant applications."
        path="/app/compliance"
      />

      <div className="max-w-[760px] mx-auto px-5 sm:px-8">
        {/* Screen-only controls */}
        <div className="tf-no-print flex items-center justify-between gap-3 mb-6">
          <Link
            to="/app"
            data-testid="compliance-back"
            className="inline-flex items-center gap-1.5 font-body font-bold tf-link"
            style={{ fontSize: 13 }}
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
          <PrintButton label="Print / Save as PDF" />
        </div>

        {/* Header */}
        <header className="tf-compliance-header">
          <p
            className="font-body font-bold"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.28em',
              color: 'var(--tf-purple)',
            }}
          >
            THE FULL IMPACT · ONE-PAGE SUMMARY
          </p>
          <h1
            className="font-heading mt-2"
            data-testid="compliance-title"
            style={{
              fontSize: 'clamp(24px, 3.4vw, 30px)',
              lineHeight: 1.15,
              color: 'var(--tf-text)',
              letterSpacing: '-0.015em',
            }}
          >
            Digital accessibility &amp; bilingual readiness
          </h1>
          <p
            className="font-body mt-2"
            style={{
              fontSize: 13,
              color: 'var(--tf-text-secondary)',
              lineHeight: 1.55,
            }}
          >
            A concise summary of the accessibility standards, bilingual foundation and
            privacy posture of the campaign — prepared for funders, commissioners and
            partner organisations. Issued {today}.
          </p>
        </header>

        {/* Fact panel */}
        <div
          className="mt-6 rounded-xl p-4"
          style={{
            background: 'var(--tf-bg-secondary)',
            border: '1px solid var(--tf-card-border)',
          }}
        >
          <Row label="ORGANISATION" value="The Full Impact — Angela Moreton" testid="row-org" />
          <Row label="COVERAGE" value="England &amp; Wales (UK-focused survivor guidance)" testid="row-coverage" />
          <Row label="AUDIENCE" value="Survivors of rape, sexual violence and domestic abuse; ISVAs, IDVAs, SARC staff, social workers." testid="row-audience" />
          <Row label="LICENSING" value="Content under Creative Commons BY-NC-ND 4.0. Guided journal: All Rights Reserved." testid="row-licensing" />
          <Row label="COST TO USER" value="Free. Anonymous. No sign-up, no accounts, no tracking cookies." testid="row-cost" />
        </div>

        {/* Accessibility */}
        <Section
          eyebrow="SECTION 01"
          title="WCAG 2.2 AA measures implemented"
        >
          <ul>
            <CheckItem>
              <strong>Skip-to-content</strong> link on every page for keyboard and
              screen-reader users (WCAG 2.4.1 Bypass Blocks).
            </CheckItem>
            <CheckItem>
              <strong>Visible focus rings</strong> on all interactive elements, meeting
              the AA focus appearance requirements (WCAG 2.4.7 / 2.4.11).
            </CheckItem>
            <CheckItem>
              <strong>Minimum target size</strong> of 44&thinsp;×&thinsp;44&nbsp;px for
              buttons, tabs and mobile nav (WCAG 2.5.8 Target Size — Minimum).
            </CheckItem>
            <CheckItem>
              <strong>ARIA landmarks &amp; labels</strong> on headers, navigation,
              main and footer; abbreviations (SV, DA) exposed as accessible names.
            </CheckItem>
            <CheckItem>
              <strong>Colour contrast</strong> checked for body text, links and buttons
              against WCAG AA contrast ratios; calm purple/pink palette tested against
              white and neutral backgrounds.
            </CheckItem>
            <CheckItem>
              <strong>User-controlled Accessibility Panel</strong> — text-size scaling,
              high-contrast toggle, reduced-motion mode, dyslexia-friendly font, all
              persisted per browser via localStorage.
            </CheckItem>
            <CheckItem>
              <strong>Reduced-motion support</strong> honouring{' '}
              <code>prefers-reduced-motion</code> and the user toggle.
            </CheckItem>
            <CheckItem>
              <strong>Trauma-informed patterns</strong> — Quick Exit button on every
              page, "Ground me" breathing modal, no auto-playing media, no forced pop-ups.
            </CheckItem>
            <CheckItem>
              <strong>Semantic HTML &amp; heading hierarchy</strong> — one{' '}
              <code>h1</code> per page, logical h2/h3 order, list semantics for
              structured guidance.
            </CheckItem>
            <CheckItem>
              <strong>Print stylesheet</strong> — every long-form guide and the
              guided journal are printable / saveable as PDF for offline reading.
            </CheckItem>
          </ul>
        </Section>

        {/* Bilingual */}
        <Section
          eyebrow="SECTION 02"
          title="Bilingual English / Welsh readiness"
        >
          <p
            className="font-body"
            style={{
              fontSize: 12.5,
              color: 'var(--tf-text)',
              lineHeight: 1.6,
            }}
          >
            The application ships with a full bilingual infrastructure so that Welsh
            translation can be added by a qualified translator without any further
            engineering work.
          </p>
          <ul className="mt-2">
            <CheckItem>
              React Context-based i18n (<code>LanguageContext</code>) with a persisted
              user preference — <em>English</em> or <em>Cymraeg</em>.
            </CheckItem>
            <CheckItem>
              Central <code>translations.js</code> dictionary — navigation, actions,
              footer and page chrome are already translation-ready.
            </CheckItem>
            <CheckItem>
              <code>lang</code> attribute updated on the document root when the user
              switches language (WCAG 3.1.1 Language of Page).
            </CheckItem>
            <CheckItem>
              Regional specialist Welsh services listed in the SV support directory.
            </CheckItem>
            <CheckItem>
              A visible "Cymraeg pending" notice explains where translation is not yet
              verified by a Welsh-speaker — avoiding machine-translated content on a
              sensitive site.
            </CheckItem>
          </ul>
        </Section>

        {/* Privacy */}
        <Section
          eyebrow="SECTION 03"
          title="Privacy &amp; safeguarding posture"
        >
          <ul>
            <CheckItem>
              <strong>No accounts, no sign-up, no personal data collected.</strong>
              &nbsp;Journal entries stay in the reader&rsquo;s browser (localStorage)
              and are never uploaded.
            </CheckItem>
            <CheckItem>
              <strong>Anonymous, aggregate visit counts only</strong> — the backend
              stores per-page hit counters with no IP, user agent or fingerprint.
            </CheckItem>
            <CheckItem>
              <strong>Quick Exit</strong> button visible on every page — one tap
              redirects to a neutral site and replaces history entry.
            </CheckItem>
            <CheckItem>
              <strong>Admin analytics</strong> gated behind an environment key and
              hidden from the public interface.
            </CheckItem>
            <CheckItem>
              <strong>Trust signals</strong> —{' '}
              <code>/.well-known/security.txt</code>, JSON-LD schema and canonical
              meta tags, deployed to counter false-positive spam classification by
              gateway vendors (Sophos, Cisco Talos).
            </CheckItem>
          </ul>
        </Section>

        {/* Sourcing */}
        <Section
          eyebrow="SECTION 04"
          title="Content sourcing &amp; editorial review"
        >
          <p
            className="font-body"
            style={{
              fontSize: 12.5,
              color: 'var(--tf-text)',
              lineHeight: 1.6,
            }}
          >
            Survivor-facing information is drawn from reputable UK sources and
            reviewed against the current Victims&rsquo; Code of Practice.
          </p>
          <ul className="mt-2">
            <CheckItem>Rape Crisis England &amp; Wales</CheckItem>
            <CheckItem>Crown Prosecution Service (CPS)</CheckItem>
            <CheckItem>Victims&rsquo; Code of Practice</CheckItem>
            <CheckItem>gov.uk, NHS, Mind, PTSD UK</CheckItem>
            <CheckItem>Refuge, Women&rsquo;s Aid, Galop, Karma Nirvana, ManKind</CheckItem>
          </ul>
        </Section>

        {/* Footer strip */}
        <footer
          className="mt-10 pt-5 tf-compliance-footer"
          style={{ borderTop: '1px solid var(--tf-card-border)' }}
        >
          <p
            className="font-body"
            style={{ fontSize: 11, color: 'var(--tf-text-muted)', lineHeight: 1.6 }}
          >
            © {new Date().getFullYear()} Angela Moreton · The Full Impact™.
            This summary describes the state of the platform on the date
            issued and is provided for information only. It is not a substitute for
            an independent accessibility audit. Enquiries:{' '}
            <a
              href="https://www.instagram.com/thefullimpactcampaign"
              className="tf-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              @thefullimpactcampaign
            </a>
            .
          </p>
        </footer>
      </div>
    </div>
  );
}
